'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { IssueReportDialog } from '@/components/issue-report/issue-report-dialog';
import { ReasonTip } from '@/components/ui/reason-tip';
import type { IssueReportAuth, IssueReportSource } from '@/lib/api/issue-reports';

/**
 * 도움말을 띄운 화면의 신고 문맥. 작성 화면은 머리말 「문제 신고」와 같은 값을 넘긴다 - 그래야 회원 신고에 task
 * 문맥이 붙고, 신청자는 로그인 없이 작성 링크로 신고한다(점검 N-S17). 넘기지 않으면 대시보드 신고다.
 */
export interface HelpIssueReportContext {
  auth: IssueReportAuth | null;
  sourceType: IssueReportSource;
  sourceAccessId?: string;
  /** auth 가 없을 때 잠긴 단추의 말풍선. */
  disabledReason?: string;
}

export function HelpFeedback({
  docId,
  onBeforeOpen,
  issueReport,
}: {
  docId: string;
  onBeforeOpen?: () => void;
  issueReport?: HelpIssueReportContext;
}) {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const token = (session as { accessToken?: string } | null)?.accessToken;
  const memberAuth: IssueReportAuth | null = token ? { kind: 'member', accessToken: token } : null;
  const auth = issueReport ? issueReport.auth : memberAuth;

  return (
    <>
      <div className="border-t border-border pt-5 text-sm text-muted-foreground">
        <span>도움이 더 필요한가요?</span>{' '}
        {issueReport || auth ? (
          <ReasonTip reason={auth ? null : issueReport?.disabledReason}>
            <button
              type="button"
              disabled={!auth}
              className="font-medium text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:text-muted-foreground disabled:no-underline"
              onClick={() => { onBeforeOpen?.(); setOpen(true); }}
            >
              문제 신고
            </button>
          </ReasonTip>
        ) : (
          <Link href={`/?callbackUrl=${encodeURIComponent(`/help/${docId}`)}`} className="font-medium text-primary hover:underline">
            로그인 후 문제 신고
          </Link>
        )}
      </div>
      <IssueReportDialog
        open={open}
        onOpenChange={setOpen}
        auth={auth}
        sourceType={issueReport?.sourceType ?? 'DASHBOARD'}
        sourceAccessId={issueReport?.sourceAccessId}
        helpDocId={docId}
      />
    </>
  );
}
